/* ============================================================
   IMENHOF BLOOM — a11y-drawers.js
   Gedeeld toegankelijkheidscontract voor alle schuifvensters
   (winkelwagen, zoeken, mobiel menu) volgens het WCAG 2.2 AA
   dialog/drawer-patroon.

   Werkt als verrijkingslaag bovenop theme.js: theme.js blijft de
   .is-open class togglen en de scroll-lock beheren; deze laag voegt
   focus-trap, focus-return, aria-expanded-sync, inert-achtergrond,
   correcte aria-hidden-afhandeling en een betrouwbare live-region
   voor winkelwagen-meldingen toe. Eén bron van waarheid voor alle
   drie de drawers.
   ============================================================ */
(function () {
  'use strict';

  var DRAWER_IDS = ['CartDrawer', 'SearchDrawer', 'MenuDrawer', 'FilterDrawer'];
  var FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  /* ---------- Live-region voor meldingen (add-to-cart e.d.) ----------
     Moet permanent in de DOM staan vóór de eerste update, anders kondigt
     een schermlezer de wijziging niet betrouwbaar aan. */
  var live = document.createElement('div');
  live.id = 'IMHLiveRegion';
  live.className = 'visually-hidden';
  live.setAttribute('role', 'status');
  live.setAttribute('aria-live', 'polite');
  live.setAttribute('aria-atomic', 'true');

  function mountLive() {
    if (!live.isConnected && document.body) document.body.appendChild(live);
  }

  function announce(msg) {
    if (!msg) return;
    mountLive();
    // Eerst leegmaken, dan zetten: zo wordt ook een identieke melding
    // (twee keer hetzelfde product toevoegen) opnieuw aangekondigd.
    live.textContent = '';
    window.setTimeout(function () { live.textContent = msg; }, 60);
  }
  window.IMHAnnounce = announce;

  /* ---------- Helpers ---------- */
  function drawerById(id) { return document.getElementById(id); }

  function openDrawers() {
    return DRAWER_IDS
      .map(drawerById)
      .filter(function (d) { return d && d.classList.contains('is-open'); });
  }

  function syncRootDrawerState() {
    document.documentElement.classList.toggle('is-drawer-open', openDrawers().length > 0);
  }

  function triggerFor(id) {
    return document.querySelector('[data-drawer-open="' + id + '"], [aria-controls="' + id + '"]');
  }

  function panelOf(drawer) {
    return drawer.querySelector('.drawer__panel') || drawer;
  }

  function visibleFocusables(panel) {
    return Array.prototype.filter.call(
      panel.querySelectorAll(FOCUSABLE),
      function (n) { return n.getClientRects().length > 0; }
    );
  }

  /* ---------- Achtergrond afschermen (inert + aria-hidden) ----------
     Alle directe body-children behalve de actieve drawer en de
     live-region worden onbereikbaar voor toetsenbord én schermlezer.
     inert dekt moderne browsers; aria-hidden is de vangnet-laag. */
  function backgroundChildren(activeDrawer) {
    return Array.prototype.filter.call(document.body.children, function (node) {
      if (node === activeDrawer || node === live) return false;
      var tag = node.tagName;
      return tag !== 'SCRIPT' && tag !== 'TEMPLATE' && tag !== 'STYLE' && tag !== 'LINK';
    });
  }

  function shieldBackground(activeDrawer) {
    backgroundChildren(activeDrawer).forEach(function (node) {
      node.setAttribute('inert', '');
      node.setAttribute('aria-hidden', 'true');
    });
  }

  function unshieldBackground(activeDrawer) {
    backgroundChildren(activeDrawer).forEach(function (node) {
      node.removeAttribute('inert');
      node.removeAttribute('aria-hidden');
    });
  }

  /* ---------- Open / sluit-reactie per drawer ---------- */
  var openerMap = {};

  function handleToggle(drawer) {
    var isOpen = drawer.classList.contains('is-open');
    if (drawer._a11yOpen === isOpen) return; // geen echte statuswijziging
    drawer._a11yOpen = isOpen;
    syncRootDrawerState();

    var id = drawer.id;
    var trig = triggerFor(id);

    if (isOpen) {
      // Trigger is zojuist geactiveerd → bewaar het bronelement vóór we
      // de focus verplaatsen, zodat we hem na sluiten kunnen teruggeven.
      var src = document.activeElement;
      openerMap[id] = (src && src !== document.body && src.focus) ? src : trig;

      drawer.removeAttribute('aria-hidden');           // venster zichtbaar voor AT
      if (trig) trig.setAttribute('aria-expanded', 'true');
      shieldBackground(drawer);                          // achtergrond afschermen

      // Beginfocus in het venster.
      var panel = panelOf(drawer);
      var target = panel.querySelector(FOCUSABLE);
      if (target) focusSafe(target);
    } else {
      drawer.setAttribute('aria-hidden', 'true');
      if (trig) trig.setAttribute('aria-expanded', 'false');

      var others = openDrawers();
      if (others.length === 0) {
        unshieldBackground(drawer);
      } else {
        // Zeldzaam: een ander venster staat nog open → herbereken afscherming.
        unshieldBackground(drawer);
        shieldBackground(others[0]);
      }

      // Focus terug naar het oorspronkelijke bronelement.
      var opener = openerMap[id];
      if (opener && opener.isConnected) focusSafe(opener);
      openerMap[id] = null;
    }
  }

  function focusSafe(el) {
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  }

  /* ---------- Focus-trap (vangnet naast inert) ---------- */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var drawer = openDrawers()[0];
    if (!drawer) return;

    var panel = panelOf(drawer);
    var nodes = visibleFocusables(panel);
    if (!nodes.length) { e.preventDefault(); return; }

    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    var active = document.activeElement;

    if (!panel.contains(active)) {
      e.preventDefault();
      focusSafe(first);
    } else if (e.shiftKey && active === first) {
      e.preventDefault();
      focusSafe(last);
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      focusSafe(first);
    }
  }, true);

  /* ---------- Toast spiegelen naar de live-region ----------
     theme.js maakt een <div class="toast"> aan en toont 'm met .is-show.
     Die node krijgt geen role/aria-live, dus we kondigen de tekst zelf
     aan via onze permanente live-region en markeren de visuele toast
     als decoratief voor AT. */
  function wireToast(toast) {
    if (!toast || toast._a11yWired) return;
    toast._a11yWired = true;
    toast.setAttribute('aria-hidden', 'true');

    var lastMsg = '';
    var mo = new MutationObserver(function () {
      if (!toast.classList.contains('is-show')) return;
      var msg = (toast.textContent || '').replace('✓', '').trim();
      if (msg && msg !== lastMsg) { lastMsg = msg; announce(msg); }
      else if (msg) { announce(msg); } // herhaalde melding toch aankondigen
    });
    mo.observe(toast, {
      attributes: true, attributeFilter: ['class'],
      childList: true, characterData: true, subtree: true
    });
  }

  function watchForToast() {
    var existing = document.querySelector('.toast');
    if (existing) { wireToast(existing); return; }
    // Toast wordt pas bij de eerste melding aangemaakt → wacht erop.
    var bodyMo = new MutationObserver(function () {
      var t = document.querySelector('.toast');
      if (t) { wireToast(t); bodyMo.disconnect(); }
    });
    if (document.body) bodyMo.observe(document.body, { childList: true });
  }

  /* ---------- Init ---------- */
  function init() {
    mountLive();

    DRAWER_IDS.forEach(function (id) {
      var drawer = drawerById(id);
      if (!drawer) return;

      // Drawers moeten directe body-kinderen zijn. Staat een drawer geneste in een
      // sectie (zoals MenuDrawer in de header-sectie), dan zet shieldBackground()
      // hieronder inert op die voorouder-sectie en bevriest zo de drawer zélf — dat
      // brak het mobiele menu (open maar niet klikbaar/sluitbaar). Verplaats 'm los.
      if (drawer.parentNode !== document.body) document.body.appendChild(drawer);

      drawer._a11yOpen = drawer.classList.contains('is-open');

      // Trigger-semantiek borgen (vangnet als de markup niet is bijgewerkt).
      var trig = triggerFor(id);
      if (trig) {
        if (!trig.hasAttribute('aria-haspopup')) trig.setAttribute('aria-haspopup', 'dialog');
        if (!trig.hasAttribute('aria-controls')) trig.setAttribute('aria-controls', id);
        if (!trig.hasAttribute('aria-expanded')) {
          trig.setAttribute('aria-expanded', drawer._a11yOpen ? 'true' : 'false');
        }
      }

      var mo = new MutationObserver(function () { handleToggle(drawer); });
      mo.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    });

    syncRootDrawerState();
    watchForToast();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
